import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { AppBackground } from '@/components/AppBackground';
import { FloatingInea } from '@/components/FloatingInea';
import { InstallPrompt } from '@/components/InstallPrompt';
import { IneaContextProvider } from '@/components/IneaContext';
import { AppShell } from '@/components/AppShell';
import LoginPage from '@/pages/LoginPage';
import InvitePage from '@/pages/InvitePage';
import SsoCallbackPage from '@/pages/SsoCallbackPage';
import { useFeatures } from '@/hooks/useFeatures';
import DashboardPage from '@/pages/DashboardPage';
import OnboardingPage from '@/pages/OnboardingPage';
import ProfilePage from '@/pages/ProfilePage';
import DiagnosticPage from '@/pages/DiagnosticPage';
import IdentityPage from '@/pages/IdentityPage';
import ContentPage from '@/pages/ContentPage';
import AuditPage from '@/pages/AuditPage';
import PipelinePage from '@/pages/PipelinePage';
import FollowUpsPage from '@/pages/FollowUpsPage';
import SocialConnectPage from '@/pages/SocialConnectPage';

// Visuals page bundles Fabric.js (heavy) — load it only when the studio opens.
const VisualsPage = lazy(() => import('@/pages/VisualsPage'));
const LibraryPage = lazy(() => import('@/pages/LibraryPage'));
// Sequence builder bundles React Flow (~200kb) — lazy.
const SequencesPage = lazy(() => import('@/pages/SequencesPage'));
const SequenceBuilderPage = lazy(() => import('@/pages/SequenceBuilderPage'));
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'));
const InboxPage = lazy(() => import('@/pages/InboxPage')); // lot 3 (2026-10-28) — boîte de réception v2 sur /conversations
const PartnerIneaSettingsPage = lazy(() => import('@/pages/PartnerIneaSettingsPage'));
// Lot 2 (2026-09-03) — publication : pages derrière le flag serveur publishing_enabled.
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const AccountsPage = lazy(() => import('@/pages/AccountsPage'));
// Module Automatisations (lot 1a, 2026-10-28) — derrière le drapeau automations_enabled.
const AutomationsPage = lazy(() => import('@/pages/automations/AutomationsPage'));
const TemplatesPage = lazy(() => import('@/pages/automations/TemplatesPage'));
const ProductsPage = lazy(() => import('@/pages/automations/ProductsPage'));
const AutomationStatsPage = lazy(() => import('@/pages/automations/AutomationStatsPage'));
const AutomationSettingsPage = lazy(() => import('@/pages/automations/AutomationSettingsPage'));
const AutomationWizardPage = lazy(() => import('@/pages/automations/wizard/AutomationWizardPage')); // lot 2c

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="animate-spin text-pink" size={32} />
    </div>
  );
}

/** Lot A (2026-07-20) / Lot 1 (2026-09-03) — META masqué : les écrans qui
 *  reposent sur les DM Meta ou les connecteurs (connexion sociale, séquences,
 *  intégrations) n'existent plus pour les membres tant que le flag runtime
 *  est off (redirect tableau de bord). Réactivable d'un flip d'env. */
function MetaGate({ children }: { children: React.ReactNode }) {
  const { meta_features_enabled: metaEnabled, loaded } = useFeatures();
  if (!loaded) return <FullScreenLoader />;
  if (!metaEnabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Lot 2 (2026-09-03) — publication éteinte : /calendrier et /comptes n'existent pas. */
function PublishingGate({ children }: { children: React.ReactNode }) {
  const { publishing_enabled: enabled, loaded } = useFeatures();
  if (!loaded) return <FullScreenLoader />;
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Module Automatisations (lot 1a, 2026-10-28) — drapeau éteint : les pages n'existent pas. */
function AutomationsGate({ children }: { children: React.ReactNode }) {
  const { automations_enabled: enabled, loaded } = useFeatures();
  if (!loaded) return <FullScreenLoader />;
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Protects routes — redirects to /login when unauthenticated. */
function Protected({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Gate for the main app — sends users who haven't finished onboarding
 * to the Inea chat first. Wraps content in the AppShell (sidebar + content).
 */
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated, user } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && !user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return <AppShell>{children}</AppShell>;
}

/** Redirects authenticated users away from /login. */
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <IneaContextProvider>
      <AppBackground />
      <FloatingInea />
      <InstallPrompt />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route
          path="/invite/15j"
          element={
            <PublicOnly>
              <InvitePage />
            </PublicOnly>
          }
        />
        {/* SSO Mars (Lot A) — publique : pose la session puis redirige. */}
        <Route path="/sso-callback" element={<SsoCallbackPage />} />
        <Route
          path="/onboarding"
          element={
            <Protected>
              <OnboardingPage />
            </Protected>
          }
        />
        <Route
          path="/profil"
          element={
            <Protected>
              <ProfilePage />
            </Protected>
          }
        />
        <Route
          path="/diagnostic"
          element={
            <RequireOnboarding>
              <DiagnosticPage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/identite"
          element={
            <RequireOnboarding>
              <IdentityPage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/contenu"
          element={
            <RequireOnboarding>
              <ContentPage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/audit"
          element={
            <RequireOnboarding>
              <AuditPage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/prospects"
          element={
            <RequireOnboarding>
              <PipelinePage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/relances"
          element={
            <RequireOnboarding>
              <FollowUpsPage />
            </RequireOnboarding>
          }
        />
        <Route
          path="/studio"
          element={
            <RequireOnboarding>
              <Suspense fallback={<FullScreenLoader />}>
                <VisualsPage />
              </Suspense>
            </RequireOnboarding>
          }
        />
        <Route
          path="/calendrier"
          element={
            <RequireOnboarding>
              <PublishingGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <CalendarPage />
                </Suspense>
              </PublishingGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/comptes"
          element={
            <RequireOnboarding>
              <PublishingGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AccountsPage />
                </Suspense>
              </PublishingGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/bibliotheque"
          element={
            <RequireOnboarding>
              <Suspense fallback={<FullScreenLoader />}>
                <LibraryPage />
              </Suspense>
            </RequireOnboarding>
          }
        />
        <Route
          path="/connexion-sociale"
          element={
            <RequireOnboarding>
              <MetaGate>
                <SocialConnectPage />
              </MetaGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/integrations"
          element={
            <RequireOnboarding>
              <MetaGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <IntegrationsPage />
                </Suspense>
              </MetaGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AutomationsPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/nouvelle"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AutomationWizardPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/:id"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AutomationWizardPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/modeles"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <TemplatesPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/produits"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <ProductsPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/statistiques"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AutomationStatsPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/automatisations/reglages"
          element={
            <RequireOnboarding>
              <AutomationsGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <AutomationSettingsPage />
                </Suspense>
              </AutomationsGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/sequences"
          element={
            <RequireOnboarding>
              <MetaGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <SequencesPage />
                </Suspense>
              </MetaGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/sequences/:id"
          element={
            <RequireOnboarding>
              <MetaGate>
                <Suspense fallback={<FullScreenLoader />}>
                  <SequenceBuilderPage />
                </Suspense>
              </MetaGate>
            </RequireOnboarding>
          }
        />
        <Route
          path="/conversations"
          element={
            <RequireOnboarding>
              <Suspense fallback={<FullScreenLoader />}>
                <InboxPage />
              </Suspense>
            </RequireOnboarding>
          }
        />
        <Route
          path="/parametres/inea"
          element={
            <RequireOnboarding>
              <Suspense fallback={<FullScreenLoader />}>
                <PartnerIneaSettingsPage />
              </Suspense>
            </RequireOnboarding>
          }
        />
        <Route
          path="/"
          element={
            <RequireOnboarding>
              <DashboardPage />
            </RequireOnboarding>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </IneaContextProvider>
  );
}
